import Image from 'next/image';
import Link from 'next/link';
import React from 'react';

type HeroProps = {
  backgroundImageUrlWeb: string;
  backgroundImageUrlMobile: string;
  backgroundImageUrlTablet?: string; // Optional: for tablet/narrow desktop widths
  title: string;
  headline: string;
  subheadline: React.ReactNode;
  highlightName: string;
  ctaPrimaryLink: {url: string, target: string};
  ctaPrimaryLabel: string;
  ctaSecondaryLink: {url: string, target: string};
  ctaSecondaryLabel: string;
  ctaBubble: string;
  ctaBubbleAlt?: string;
  ratingText: string;
  ratingImageUrl: string;
  ratingImageAlt?: string;
  trustpilotLogoUrl: string;
  trustpilotLogoAlt?: string;
  trustStatement: string;
  backgroundPosition?: 'default' | 'right';
  backgroundSizing?: 'cover' | 'auto';
};

const HeroBanner: React.FC<HeroProps> = ({
  backgroundImageUrlWeb,
  backgroundImageUrlMobile,
  backgroundImageUrlTablet,
  title,
  headline,
  subheadline,
  highlightName,
  ctaPrimaryLink,
  ctaPrimaryLabel,
  ctaSecondaryLink,
  ctaSecondaryLabel,
  ctaBubble,
  ctaBubbleAlt = "Profile photo",
  ratingText,
  ratingImageUrl,
  ratingImageAlt = "5 star rating",
  trustpilotLogoUrl,
  trustpilotLogoAlt = "Trustpilot logo",
  trustStatement,
  backgroundPosition = 'default',
  backgroundSizing = 'cover',
}) => {
  // bg-right: subject on right stays visible, vertical center avoids cropping heads
  // bg-right-bottom: legacy position for black-friday page
  const bgPositionClasses = backgroundPosition === 'right'
    ? 'bg-right-bottom'
    : 'bg-right';

  // 'auto' sizing keeps image smaller and positioned to the right (original style)
  // 'cover' fills the entire banner area
  const bgSizeClasses = backgroundSizing === 'auto'
    ? 'bg-[length:auto_70%] md:bg-[length:auto_80%] lg:bg-[length:auto_90%] xl:bg-[length:auto_100%]'
    : 'bg-cover';

  return (
    <div>
      {/* Tablet/Narrow Desktop Hero Section (sm to xl breakpoint) */}
      {backgroundImageUrlTablet && (
        <section className="hidden sm:block xl:hidden w-full px-10">
          <div
            className={`max-w-[1306px] w-full mx-auto bg-no-repeat ${bgSizeClasses} ${bgPositionClasses}`}
            style={{
              backgroundImage: `url(${backgroundImageUrlTablet})`,
              maxHeight: '600px',
            }}
          >
            <div className="py-20 px-16 text-[30px] md:text-[36px]">
              <div className="w-[60%] lg:w-full">
                <h4 className="font-normal pb-2">{title}</h4>

                <h1 className="max-w-[743px] w-full font-semibold leading-[45px] mb-9">
                  {headline}
                </h1>

                <p className="max-w-[260px] sm:max-w-[280px] md:max-w-[370px] lg:max-w-[450px] xl:max-w-[700px] w-full text-[20px] md:text-[24px] font-normal italic mb-9 relative z-30">
                  {subheadline}<span className="font-bold">{highlightName}</span>.
                </p>

                <div className="flex w-full items-start mb-[11px] overflow-visible">
                  <div className="flex flex-row h-[54px] gap-4 relative z-10">
                    <Link href={ctaPrimaryLink.url} target={ctaPrimaryLink.target}>
                      <button className="bg-[#ED7523] hover:opacity-90 text-white cursor-pointer py-3 px-6 rounded-[10px] font-medium text-[17px] md:text-[20px]">
                        {ctaPrimaryLabel}
                      </button>
                    </Link>

                    <Link href={ctaSecondaryLink.url} target={ctaSecondaryLink.target}>
                      <button className="bg-[#ED7523] hover:opacity-90 text-white cursor-pointer py-3 px-6 rounded-[10px] font-medium text-[17px] md:text-[20px]">
                        {ctaSecondaryLabel}
                      </button>
                    </Link>
                  </div>

                  {ctaBubble && (
                    <div className="flex-shrink-0 -ml-[10px] relative z-20" style={{ marginTop: '-45px', marginBottom: '-20px' }}>
                      <Image
                        src={ctaBubble}
                        alt={ctaBubbleAlt}
                        width={200}
                        height={200}
                        className="w-[200px] h-[200px] rounded-full object-cover"
                      />
                    </div>
                  )}
                </div>

                <div className="flex gap-4 mb-5 relative z-30">
                  <div className="flex items-center gap-2">
                    <Image
                      src={ratingImageUrl}
                      alt={ratingImageAlt}
                      width={165.33}
                      height={31}
                      loading="lazy"
                      className="w-[88px] lg:w-[165.33px] h-[16.5px] lg:h-[31px]"
                    />
                    <span className="f-roboto text-[16px] lg:text-[20px] font-bold">
                      {ratingText}
                    </span>
                  </div>

                  <div>
                    <Image
                      src={trustpilotLogoUrl}
                      alt={trustpilotLogoAlt}
                      width={198.54}
                      height={49}
                      loading="lazy"
                      className="w-[140px] h-[35px] lg:w-[198.54px] lg:h-[49px]"
                    />
                  </div>
                </div>

                <p className="text-[18px] lg:text-[22px] italic font-normal -mt-[10px]">{trustStatement}</p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Wide Desktop Hero Section (xl+ breakpoint or fallback if no tablet image) */}
      <section className={`hidden ${backgroundImageUrlTablet ? 'xl:block' : 'sm:block'} w-full px-10`}>
        <div
          className={`max-w-[1306px] w-full mx-auto bg-no-repeat ${bgSizeClasses} ${bgPositionClasses}`}
          style={{
            backgroundImage: `url(${backgroundImageUrlWeb})`,
            maxHeight: '600px',
          }}
        >
          <div className="py-20 px-16 text-[30px] md:text-[36px]">
            <div className="w-[60%] lg:w-full">
              <h4 className="font-normal pb-2">{title}</h4>

              <h1 className="max-w-[743px] w-full font-semibold leading-[45px] mb-9">
                {headline}
              </h1>

              <p className="max-w-[260px] sm:max-w-[280px] md:max-w-[370px] lg:max-w-[450px] xl:max-w-[700px] w-full text-[20px] md:text-[24px] font-normal italic mb-9 relative z-30">
                {subheadline}<span className="font-bold">{highlightName}</span>.
              </p>

              <div className="flex w-full items-start mb-[11px] overflow-visible">
                <div className="flex flex-row h-[54px] gap-4 relative z-10">
                  <Link href={ctaPrimaryLink.url} target={ctaPrimaryLink.target}>
                    <button className="bg-[#ED7523] hover:opacity-90 text-white cursor-pointer py-3 px-6 rounded-[10px] font-medium text-[17px] md:text-[20px]">
                      {ctaPrimaryLabel}
                    </button>
                  </Link>

                  <Link href={ctaSecondaryLink.url} target={ctaSecondaryLink.target}>
                    <button className="bg-[#ED7523] hover:opacity-90 text-white cursor-pointer py-3 px-6 rounded-[10px] font-medium text-[17px] md:text-[20px]">
                      {ctaSecondaryLabel}
                    </button>
                  </Link>
                </div>

                {ctaBubble && (
                  <div className="flex-shrink-0 -ml-[10px] relative z-20" style={{ marginTop: '-45px', marginBottom: '-20px' }}>
                    <Image
                      src={ctaBubble}
                      alt={ctaBubbleAlt}
                      width={200}
                      height={200}
                      className="w-[200px] h-[200px] rounded-full object-cover"
                    />
                  </div>
                )}
              </div>

              <div className="flex gap-4 mb-5 relative z-30">
                <div className="flex items-center gap-2">
                  <Image
                    src={ratingImageUrl}
                    alt={ratingImageAlt}
                    width={165.33}
                    height={31}
                    loading="lazy"
                    className="w-[88px] lg:w-[165.33px] h-[16.5px] lg:h-[31px]"
                  />
                  <span className="f-roboto text-[16px] lg:text-[20px] font-bold">
                    {ratingText}
                  </span>
                </div>

                <div>
                  <Image
                    src={trustpilotLogoUrl}
                    alt={trustpilotLogoAlt}
                    width={198.54}
                    height={49}
                    loading="lazy"
                    className="w-[140px] h-[35px] lg:w-[198.54px] lg:h-[49px]"
                  />
                </div>
              </div>


              <p className="text-[18px] lg:text-[22px] italic font-normal -mt-[10px]">{trustStatement}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile Hero Section */}
      <section
        className="block sm:hidden w-full"
      >
        <div
          className="w-full h-[275px] overflow-hidden items-end"
        >
          <div
            className="w-full h-[300px] bg-no-repeat bg-cover bg-right"
            style={{
              backgroundImage: `url(${backgroundImageUrlMobile})`,
            }}
          />
        </div>
        <div className="w-full text-[30px] text-center px-10 py-5">
          <h4 className="font-normal">{title}</h4>

          <h1 className="max-w-[743px] w-full font-semibold leading-tight mb-9">
            {headline}
          </h1>

          <p className="max-w-[744px] w-full text-[20px] font-normal italic mb-2">
            {subheadline}{' '}
            <span className="font-bold">{highlightName}</span>.
          </p>

          <div className="flex flex-col gap-4 mb-20">
            <Link href={ctaPrimaryLink.url} target={ctaPrimaryLink.target}>
              <button className="bg-[#ED7523] hover:opacity-90 text-white cursor-pointer py-3 px-6 rounded-[10px] font-medium text-[17px]">
                {ctaPrimaryLabel}
              </button>
            </Link>

            <Link href={ctaSecondaryLink.url} target={ctaSecondaryLink.target}>
              <button className="bg-[#ED7523] hover:opacity-90 text-white cursor-pointer py-3 px-6 rounded-[10px] font-medium text-[17px]">
                {ctaSecondaryLabel}
              </button>
            </Link>
          </div>

        </div>
      </section>
    </div>
  );
};

export default HeroBanner;
